import { createContext, useContext, useCallback, useState, ReactNode } from "react";
import { isEmployeePath } from "@/routes/employeePaths";

export const RAIL_WIDTH = 48;
export const RAIL_WIDTH_EXPANDED = 200;

interface SidebarContextValue {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

/** Below this the nav drawer opens as an overlay, so it starts closed there. */
const OVERLAY_BELOW_PX = 992;

/**
 * How the nav starts on a fresh load. skyEmployee's opens expanded on a
 * desktop-width screen (its menu reads as labels, not icons); everything else
 * keeps starting collapsed, as before.
 */
function startsCollapsed(): boolean {
  if (typeof window === "undefined") return true;
  const wide =
    typeof window.matchMedia === "function"
      ? window.matchMedia(`(min-width: ${OVERLAY_BELOW_PX}px)`).matches
      : window.innerWidth >= OVERLAY_BELOW_PX;
  return !(wide && isEmployeePath(window.location.pathname));
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsedState] = useState(startsCollapsed);

  const setIsCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsedState(collapsed);
  }, []);

  const toggle = useCallback(() => {
    setIsCollapsedState((prev) => !prev);
  }, []);

  const value: SidebarContextValue = {
    isCollapsed,
    setIsCollapsed,
    toggle,
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}

export function useSidebarOptional(): SidebarContextValue | null {
  return useContext(SidebarContext);
}
